import React, { useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import InputBase from "@material-ui/core/InputBase";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import SearchIcon from "@material-ui/icons/Search";

import { AuthContext } from "../../context/Auth/AuthContext";
import { menuGroupsConfig } from "../../utils/menuGroups";
import { itemDeMenuVisible } from "../../utils/menuPermissions";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  paper: { position: "absolute", top: 80, margin: 0, width: 600, maxWidth: "calc(100% - 32px)", borderRadius: 16 },
  head: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "14px 16px",
    borderBottom: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
  },
  input: { flexGrow: 1, fontSize: 16, color: theme.palette.text.primary },
  list: { maxHeight: 400, overflowY: "auto", padding: 6 },
  item: { borderRadius: 8 },
  group: { fontSize: 12, color: theme.palette.text.secondary },
  empty: { padding: "24px 16px", textAlign: "center", color: theme.palette.text.secondary, fontSize: 14 },
  foot: {
    padding: "10px 16px",
    borderTop: `1px solid ${theme.palette.divider}`,
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
}));

const norm = (s) =>
  String(s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");

// Paleta Ctrl+K (doc 0446): navega a cualquier página que el rol del usuario puede ver.
const CommandPalette = ({ open, onClose }) => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (open) {
      setQuery("");
      setActive(0);
    }
  }, [open]);

  const pages = useMemo(() => {
    if (!open) return [];
    const out = [];
    Object.values(menuGroupsConfig).forEach((group) =>
      (group.items || []).forEach((item) => {
        if (!itemDeMenuVisible(item, user)) return;
        if (out.some((p) => p.route === item.route)) return;
        out.push({
          route: item.route,
          label: i18n.t(item.translationKey),
          group: i18n.t(group.titleKey),
          icon: group.renderIcon(item.icon),
        });
      })
    );
    return out;
  }, [open, user]);

  const q = norm(query);
  const results = q
    ? pages.filter((p) => norm(p.label).includes(q) || norm(p.group).includes(q))
    : pages;

  const go = (page) => {
    if (!page) return;
    onClose();
    history.push(page.route);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(results[active]);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} classes={{ paper: classes.paper }} aria-label={i18n.t("tickets.simple.searchPage")}>
      <div className={classes.head}>
        <SearchIcon />
        <InputBase
          autoFocus
          className={classes.input}
          placeholder={i18n.t("tickets.simple.searchPage")}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
          }}
          onKeyDown={onKeyDown}
          inputProps={{ "aria-label": i18n.t("tickets.simple.searchPage") }}
        />
      </div>
      <List className={classes.list} dense>
        {results.length === 0 && <div className={classes.empty}>{i18n.t("tickets.simple.noResults")}</div>}
        {results.map((p, i) => (
          <ListItem
            key={p.route}
            button
            selected={i === active}
            className={classes.item}
            onMouseEnter={() => setActive(i)}
            onClick={() => go(p)}
          >
            <ListItemIcon style={{ minWidth: 36 }}>{p.icon}</ListItemIcon>
            <ListItemText primary={p.label} />
            <span className={classes.group}>{p.group}</span>
          </ListItem>
        ))}
      </List>
      <div className={classes.foot}>{i18n.t("tickets.simple.paletteHint")}</div>
    </Dialog>
  );
};

export default CommandPalette;
